import React, { useState, useEffect, useCallback } from 'react';
import {
    ArrowLeft, Bell, AlertTriangle, Pin, Settings,
    Check, ChevronRight, ChevronLeft, Calendar, Clock,
    Link as LinkIcon, Layers, Eye
} from 'lucide-react';
import RichTextEditor from '../RichTextEditor';
import AssignmentPlaceholderPicker from './AssignmentPlaceholderPicker';
import PlaceholderTextarea from './PlaceholderTextarea';

// ─── Helper: priority config ─────────────────────────────────────────────────
const getPriorityConfig = (priority) => {
    switch (priority) {
        case 'urgent':
            return { bg: 'bg-orange-100 dark:bg-orange-500/20', text: 'text-orange-600 dark:text-orange-400', label: 'Urgent', icon: <AlertTriangle className="w-4 h-4" /> };
        case 'critical':
            return { bg: 'bg-red-100 dark:bg-red-500/20', text: 'text-red-600 dark:text-red-400', label: 'Critical', icon: <AlertTriangle className="w-4 h-4" /> };
        default:
            return { bg: 'bg-blue-100 dark:bg-blue-500/20', text: 'text-blue-600 dark:text-blue-400', label: 'Normal', icon: <Bell className="w-4 h-4" /> };
    }
};

// ═══════════════════════════════════════════════════════════════════════════════
// NoticeDetailEditor — inline notice detail page within AICourseGenerator
// ═══════════════════════════════════════════════════════════════════════════════
const NoticeDetailEditor = ({ notice, timelineItem, onSave, onBack }) => {
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        priority: 'normal',
        show_popup_on_login: false,
        external_link: '',
        expiry_date: '',
        scheduled_date: '',
        assignment_placeholders: []
    });

    const [activeStep, setActiveStep] = useState('content');
    const [hasChanges, setHasChanges] = useState(false);
    const [isAnimatingIn, setIsAnimatingIn] = useState(true);

    const steps = ['content', 'settings'];
    const currentStepIndex = steps.indexOf(activeStep);
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === steps.length - 1;

    // ── Initialize ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (notice) {
            setFormData({
                title: notice.title || '',
                content: notice.content || '',
                priority: notice.priority || 'normal',
                show_popup_on_login: notice.show_popup_on_login || false,
                external_link: notice.external_link || '',
                expiry_date: notice.expiry_date || '',
                scheduled_date: notice.scheduled_date || '',
                assignment_placeholders: timelineItem?.assignment_placeholders || notice.assignment_placeholders || []
            });
            setHasChanges(false);
        }
    }, [notice, timelineItem]);

    useEffect(() => {
        const timer = setTimeout(() => setIsAnimatingIn(false), 300);
        return () => clearTimeout(timer);
    }, []);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setHasChanges(true);
    }, []);

    const handleContentChange = useCallback((content) => {
        setFormData(prev => ({ ...prev, content }));
        setHasChanges(true);
    }, []);

    const handleApplyAndBack = useCallback(() => {
        onSave(formData);
        onBack();
    }, [formData, onSave, onBack]);

    const handleBackWithoutSaving = useCallback(() => {
        if (hasChanges) {
            if (window.confirm('You have unsaved changes. Discard them?')) {
                onBack();
            }
        } else {
            onBack();
        }
    }, [hasChanges, onBack]);

    const priorityConfig = getPriorityConfig(formData.priority);
    const triggerDay = timelineItem?.trigger_day ?? timelineItem?.relative_day ?? '?';

    // ═════════════════════════════════════════════════════════════════════════
    return (
        <div className={`flex flex-col h-full bg-white dark:bg-slate-900 transition-all duration-300 ${isAnimatingIn ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}>

            {/* ── Header ────────────────────────────────────────────────── */}
            <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
                <div className="flex items-center gap-3 px-4 sm:px-6 py-3">
                    <button
                        onClick={handleBackWithoutSaving}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-slate-300 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10 rounded-lg transition-all group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                        Timeline
                    </button>

                    <div className="w-px h-6 bg-gray-200 dark:bg-slate-700" />

                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${priorityConfig.bg} ${priorityConfig.text} flex-shrink-0`}>
                            <Bell className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="font-semibold text-gray-800 dark:text-slate-100 truncate text-sm sm:text-base">
                                {formData.title || 'Untitled Notice'}
                            </h2>
                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
                                <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase ${priorityConfig.bg} ${priorityConfig.text}`}>
                                    {priorityConfig.label}
                                </span>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    Day {triggerDay}
                                </span>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={handleApplyAndBack}
                        disabled={!formData.title?.trim()}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all flex-shrink-0 ${hasChanges
                            ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white shadow-lg shadow-amber-500/25'
                            : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        {hasChanges ? (
                            <>
                                <Check className="w-4 h-4" />
                                <span className="hidden sm:inline">Apply Changes</span>
                                <span className="sm:hidden">Apply</span>
                            </>
                        ) : (
                            <>
                                <Check className="w-4 h-4" />
                                <span className="hidden sm:inline">No Changes</span>
                            </>
                        )}
                    </button>
                </div>

                {/* Step Indicator */}
                <div className="flex items-center gap-2 px-4 sm:px-6 pb-3">
                    {[
                        { id: 'content', label: 'Content', icon: Layers, step: 1 },
                        { id: 'settings', label: 'Settings', icon: Settings, step: 2 }
                    ].map(({ id, label, icon: Icon, step }, index) => {
                        const stepIdx = steps.indexOf(id);
                        const isCompleted = stepIdx < currentStepIndex;
                        const isCurrent = id === activeStep;

                        return (
                            <React.Fragment key={id}>
                                <button
                                    onClick={() => setActiveStep(id)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${isCurrent
                                        ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-500/30'
                                        : isCompleted
                                            ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-500/10'
                                            : 'text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                                        }`}
                                >
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${isCompleted
                                        ? 'bg-green-500 text-white'
                                        : isCurrent
                                            ? 'bg-amber-600 text-white'
                                            : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                                        }`}>
                                        {isCompleted ? <Check className="w-3 h-3" /> : step}
                                    </div>
                                    <span className="hidden sm:inline">{label}</span>
                                </button>
                                {index < steps.length - 1 && (
                                    <div className={`w-6 h-0.5 ${isCompleted ? 'bg-green-500' : 'bg-gray-200 dark:bg-slate-700'}`} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* ── Content Area ───────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                {activeStep === 'content' && (
                    <div className="space-y-5">
                        {/* Title */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="Enter notice title"
                                className="w-full px-4 py-3 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-amber-500 dark:focus:ring-amber-400 focus:border-transparent text-lg"
                            />
                        </div>

                        {/* Priority */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                Priority
                            </label>
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { value: 'normal', label: 'Normal', color: 'blue' },
                                    { value: 'urgent', label: 'Urgent', color: 'orange' },
                                    { value: 'critical', label: 'Critical', color: 'red' }
                                ].map(p => (
                                    <button
                                        key={p.value}
                                        type="button"
                                        onClick={() => { setFormData(prev => ({ ...prev, priority: p.value })); setHasChanges(true); }}
                                        className={`p-3 border-2 rounded-xl flex flex-col items-center gap-1.5 transition-all text-sm font-medium ${formData.priority === p.value
                                            ? `border-${p.color}-500 bg-${p.color}-50 dark:bg-${p.color}-500/10 text-${p.color}-700 dark:text-${p.color}-400`
                                            : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:border-gray-300'
                                            }`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Content with Placeholder Support */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                Content
                            </label>
                            <PlaceholderTextarea
                                value={formData.content}
                                onChange={(e) => handleContentChange(e.target.value)}
                                placeholder="Write your notice content here — use {employee.name} etc. for personalization"
                                rows={6}
                            />
                        </div>

                        {/* Assignment Placeholders */}
                        <div className="pt-4 border-t border-amber-100 dark:border-slate-700">
                            <AssignmentPlaceholderPicker
                                value={formData.assignment_placeholders || []}
                                onChange={(val) => {
                                    setFormData(prev => ({ ...prev, assignment_placeholders: val }));
                                    setHasChanges(true);
                                }}
                            />
                        </div>
                    </div>
                )}

                {activeStep === 'settings' && (
                    <div className="space-y-5">
                        {/* Show Popup on Login */}
                        <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                            <input
                                type="checkbox"
                                name="show_popup_on_login"
                                checked={formData.show_popup_on_login}
                                onChange={handleChange}
                                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                            <div>
                                <div className="flex items-center gap-2">
                                    <Eye className="w-4 h-4 text-amber-500" />
                                    <span className="text-sm font-medium text-gray-800 dark:text-slate-100">Show Popup on Login</span>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Display this notice as a popup when employees log in</p>
                            </div>
                        </label>

                        {/* External Link */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                External Link
                            </label>
                            <div className="relative">
                                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                                <input
                                    type="url"
                                    name="external_link"
                                    value={formData.external_link}
                                    onChange={handleChange}
                                    placeholder="https://example.com"
                                    className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Date Fields */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                    Scheduled Date
                                </label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                                    <input
                                        type="date"
                                        name="scheduled_date"
                                        value={formData.scheduled_date}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 text-gray-900 dark:text-white border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                    Expiry Date
                                </label>
                                <div className="relative">
                                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                                    <input
                                        type="date"
                                        name="expiry_date"
                                        value={formData.expiry_date}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 text-gray-900 dark:text-white border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Footer ─────────────────────────────────────────────────── */}
            <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 px-4 sm:px-6 py-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400">
                        {hasChanges ? (
                            <>
                                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                                <span className="text-xs">Unsaved changes</span>
                            </>
                        ) : (
                            <>
                                <div className="w-2 h-2 bg-green-500 rounded-full" />
                                <span className="text-xs">Up to date</span>
                            </>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {!isFirstStep && (
                            <button
                                onClick={() => setActiveStep(steps[currentStepIndex - 1])}
                                className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-sm"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Back
                            </button>
                        )}
                        {!isLastStep && (
                            <button
                                onClick={() => setActiveStep(steps[currentStepIndex + 1])}
                                className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors text-sm"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        )}
                        {isLastStep && (
                            <button
                                onClick={handleApplyAndBack}
                                disabled={!formData.title?.trim()}
                                className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white rounded-lg transition-all shadow-lg shadow-amber-500/25 text-sm font-semibold disabled:opacity-50"
                            >
                                <Check className="w-4 h-4" />
                                Apply & Return
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NoticeDetailEditor;
